import React from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  User,
  ClipboardCheck,
  Heart,
  ChartColumnIncreasing,
  PiggyBank,
  Wallet,
  Calculator,
  Sparkles,
  ListTodo,
  BetweenVerticalEnd,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';

const ProgressionFlowModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const steps = [
    {
      number: 1,
      title: 'Complete Client Profile',
      description: 'Start by filling out all your business and personal information in the Client Profile section. This foundational data will be used throughout your exit planning journey.',
      reason: 'Having complete profile information ensures accurate calculations and personalized recommendations.',
      icon: User,
      iconColor: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      buttonColor: 'bg-gray-600 hover:bg-gray-700',
      link: '/business-profile'
    },
    {
      number: 2,
      title: 'Business Gap Assessment',
      description: 'Complete the comprehensive Business Gap Assessment to evaluate your company\'s attractiveness and readiness for exit across multiple dimensions.',
      reason: 'Identifies strengths and weaknesses in your business operations, helping you focus on critical areas for improvement.',
      icon: ClipboardCheck,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      link: '/gap-analysis'
    },
    {
      number: 3,
      title: 'Personal Gap Assessment',
      description: 'Evaluate your personal financial, wellbeing, and spiritual readiness for transitioning out of your business.',
      reason: 'A successful exit requires personal preparation beyond just business metrics. This assessment ensures you\'re holistically ready.',
      icon: Heart,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      buttonColor: 'bg-green-600 hover:bg-green-700',
      link: '/personal-gap-analysis'
    },
    {
      number: 4,
      title: 'Calculate Range of Value',
      description: 'Use the valuation calculator to determine your business\'s current market value range using industry-standard methodologies.',
      reason: 'Understanding your business value is critical for setting realistic exit goals and making informed decisions.',
      icon: Calculator,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      buttonColor: 'bg-indigo-600 hover:bg-indigo-700',
      link: '/valuation'
    },
    {
      number: 5,
      title: 'Complete Value Gap Analysis',
      description: 'Calculate the impact of improving your business attractiveness and readiness on your exit value.',
      reason: 'Shows how addressing business gaps can significantly increase your business value and sale price.',
      icon: ChartColumnIncreasing,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
      link: '/dashboard'
    },
    {
      number: 6,
      title: 'Analyze Profit Gap',
      description: 'Compare your current profit margins against industry benchmarks to identify improvement opportunities.',
      reason: 'Higher profitability directly increases business value and makes your company more attractive to buyers.',
      icon: PiggyBank,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      buttonColor: 'bg-purple-600 hover:bg-purple-700',
      link: '/dashboard'
    },
    {
      number: 7,
      title: 'Review Wealth Gap',
      description: 'Calculate whether the proceeds from your business exit will meet your post-exit financial needs and lifestyle goals.',
      reason: 'Ensures you won\'t face financial shortfalls after transitioning from your business.',
      icon: Wallet,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
      link: '/dashboard'
    },
    {
      number: 8,
      title: 'Business Envisioning',
      description: 'Clarify your 3–10 year vision for business growth, value creation, and exit strategy. Define your purpose, goals, and roadmap.',
      reason: 'A clear business vision guides strategic decisions and helps align all stakeholders toward a successful exit.',
      icon: Sparkles,
      iconColor: 'text-rose-600',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      buttonColor: 'bg-rose-600 hover:bg-rose-700',
      link: '/envisioning?tab=business'
    },
    {
      number: 9,
      title: 'Personal Envisioning',
      description: 'Define your personal vision and life goals beyond business exit. Plan for your post-exit identity, relationships, and purpose.',
      reason: 'Personal preparation is essential for a fulfilling life after business transition, preventing post-exit regret.',
      icon: Sparkles,
      iconColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      buttonColor: 'bg-pink-600 hover:bg-pink-700',
      link: '/envisioning?tab=personal'
    },
    {
      number: 10,
      title: 'Work on Task Manager',
      description: 'Begin executing your action plan by working through prioritized tasks generated from your assessments.',
      reason: 'Systematic task completion ensures steady progress toward your exit goals and keeps you on track.',
      icon: ListTodo,
      iconColor: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
      buttonColor: 'bg-cyan-600 hover:bg-cyan-700',
      link: '/task-manager'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold mb-1">Your Exit Planning Journey</h2>
            <p className="text-blue-100 text-sm">Follow these 10 steps to prepare for a successful business exit</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Steps */}
        <div className="p-6">
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`border-2 ${step.borderColor} rounded-xl p-6 ${step.bgColor} hover:shadow-lg transition-all duration-200`}
              >
                <div className="flex items-start gap-4">
                  {/* Step Number Circle */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center font-bold text-lg text-gray-700 shadow-sm">
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-white`}>
                          <step.icon className={`h-6 w-6 ${step.iconColor}`} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-3 leading-relaxed">
                      {step.description}
                    </p>

                    <div className="bg-white bg-opacity-60 rounded-lg p-3 mb-4 border border-gray-200">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-800">Why this matters:</span> {step.reason}
                      </p>
                    </div>

                    <Link
                      to={step.link}
                      onClick={onClose}
                      className={`inline-flex items-center gap-2 px-4 py-2 ${step.buttonColor} text-white rounded-lg hover:shadow-md transition-all duration-200 font-medium`}
                    >
                      <span>Go to {step.title}</span>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                {/* Progress Connector (except for last item) */}
                {index < steps.length - 1 && (
                  <div className="ml-6 mt-4 mb-2 border-l-4 border-dashed border-gray-300 h-6"></div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-600 rounded-full">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Ready to Begin?</h4>
                <p className="text-gray-700 mb-4">
                  Start with Step 1 and work your way through each phase. You can always return to this guide from the dashboard.
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium min-h-[44px]"
                >
                  Let's Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressionFlowModal;
